import React from 'react';
import * as Form from 'react-hook-form';
import { t } from '@lingui/macro';

import SetupUserInfo from '@src/api/SetupUserInfo';
import { Gender } from '@src/enums';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useIsMounted from '@src/hooks/useIsMounted';
import { i18n, i18yup } from '@src/i18n';

import { FormData } from './types';

const useForm = () => {
  const [loading, setLoading] = React.useState(false);
  const {
    tokens: { accessToken },
  } = useAuthenticationTokens();
  const isMounted = useIsMounted();
  const axiosErrorHandler = useAxiosErrorHandler();

  const form = Form.useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    validationSchema: i18yup.object<FormData>({
      name: i18yup
        .string()
        .required()
        .label(i18n._(t`Name`)),
      birthday: i18yup
        .date()
        .max(new Date())
        .required()
        .label(i18n._(t`Birthday`)),
      gender: i18yup
        .string()
        .oneOf([Gender.Male, Gender.Female, Gender.RatherNotSay])
        .default(Gender.RatherNotSay)
        .required()
        .label(i18n._(t`Gender`)),
    }),
  });

  const handleSubmit = React.useCallback(
    async ({ birthday, gender, name }: FormData) => {
      try {
        setLoading(true);
        await SetupUserInfo(accessToken, {
          birthday: birthday.toISOString(),
          gender,
          name,
        });
      } catch (error) {
        axiosErrorHandler(error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    },
    [accessToken, axiosErrorHandler, isMounted]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
    }),
    [form, handleSubmit, loading]
  );
};

export default useForm;
